import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentButtonManager } from '@ctct/application-layer';
import { BillingStrategy, type BillingStrategyQuery } from '@ctct/domain-layer';
import type {
  PaymentButtonHooks,
  PaymentButtonPageStore,
  PaymentMethodsHooks,
  PrepaySelectHooks,
} from '@ctct/state-client-layer';
import { useAccountId, useBillingInfo, usePartnerName, usePayForPlanMutation } from '@ctct/state-server-layer';

import { useIsMobile } from '../../hooks';
import { useIsSingleBillChildAccount } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<PaymentButtonManager, 'paymentButtonInit'>;
  pageStore: PaymentButtonPageStore;
  hooks: PaymentButtonHooks;
  externalHooks: {
    usePrepay: PrepaySelectHooks['usePrepay'];
    useHasPendingExternalRedirect: PaymentMethodsHooks['useHasPendingExternalRedirect'];
  };
  version?: 'standard' | 'split-terms-text';
  billingStrategy: BillingStrategyQuery;
  isCustomer?: boolean;
}

const resolveBillingStrategyEnum = (billingStrategy: BillingStrategyQuery): BillingStrategy =>
  billingStrategy.isPending ? BillingStrategy.InHouse : billingStrategy.data;

export function usePaymentButtonWidget({
  initializer,
  pageStore,
  hooks,
  externalHooks,
  version = 'standard',
  billingStrategy,
  isCustomer,
}: Props) {
  const { t } = useTranslation();
  const payForPlan = usePayForPlanMutation();
  const { isMobile } = useIsMobile();
  const hasPendingExternalRedirect = externalHooks.useHasPendingExternalRedirect();

  const partnerNameQuery = usePartnerName();
  const billingInfoQuery = useBillingInfo();
  const accountIdQuery = useAccountId();
  const accountId = accountIdQuery.data ?? undefined;
  const isSingleBillChildAccount = useIsSingleBillChildAccount({ billingStrategy });
  const resolvedBillingStrategy = resolveBillingStrategyEnum(billingStrategy);

  const componentProps = useMemo(
    () => ({
      ...initializer.paymentButtonInit({
        pageStore,
        queries: [partnerNameQuery, billingInfoQuery],
        mutations: [payForPlan],
        t,
        isMobile,
        hasPendingExternalRedirect,
        billingStrategy: resolvedBillingStrategy,
        accountId,
        isSingleBillChildAccount: Boolean(isSingleBillChildAccount),
      }),
      version,
      hooks,
      externalHooks,
      isCustomer,
    }),
    [
      initializer,
      pageStore,
      partnerNameQuery,
      billingInfoQuery,
      payForPlan,
      t,
      hooks,
      isMobile,
      hasPendingExternalRedirect,
      externalHooks,
      version,
      resolvedBillingStrategy,
      isCustomer,
      accountId,
      isSingleBillChildAccount,
    ]
  );

  return componentProps;
}

export type PaymentButtonWidgetProps = ReturnType<typeof usePaymentButtonWidget>;
